/**
 * VersionChipView — presentational deployed-version indicator. A discreet chip
 * (`⬢ v{version} · {shortSha}` + a traffic-light health dot) that toggles a
 * detail popover on click. Pure over its `frontend`/`backend` props so every
 * drift state can be rendered directly in tests without touching the network.
 *
 * The container `VersionChip` wires `BUILD_INFO` + `useBackendVersion()` into
 * these props; this component owns only the open/close interaction (click,
 * click-outside, Escape) — mirroring the Navbar user-menu dropdown idiom.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import type { BuildInfo } from './build-info'
import type { BackendVersionInfo } from './useBackendVersion'
import { type VersionDriftStatus, resolveVersionDriftStatus } from './version-drift'
import { VersionDetailPopover } from './VersionDetailPopover'
import { useClickOutside } from '@/shared/hooks'

interface VersionChipViewProps {
  frontend: BuildInfo
  backend: BackendVersionInfo | undefined
  /**
   * Direction the detail popover opens. Defaults to `'top'` because the chip
   * now lives in a bottom footer, where a below-anchored popover would be
   * clipped by the viewport edge.
   */
  placement?: 'top' | 'bottom'
}

/** Tailwind background token for the health dot per drift status. */
const HEALTH_DOT_CLASS: Record<VersionDriftStatus, string> = {
  'in-sync': 'bg-crypto-success',
  drift: 'bg-crypto-warning',
  unknown: 'bg-crypto-muted',
}

export const VersionChipView: React.FC<VersionChipViewProps> = ({
  frontend,
  backend,
  placement = 'top',
}) => {
  const { t } = useTranslation('shared')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const driftStatus = resolveVersionDriftStatus(frontend.gitSha, backend?.gitSha)

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), [])

  // Close on outside click (mirrors the Navbar user-menu dropdown).
  useClickOutside(containerRef, () => setIsOpen(false), { enabled: isOpen })

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        data-testid="version-chip"
        onClick={toggleOpen}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={t('version.chipAria')}
        title={t(`version.${driftStatus === 'in-sync' ? 'inSync' : driftStatus}`)}
        className="flex items-center gap-1.5 rounded-lg border border-crypto-border/50 bg-crypto-card px-2 py-1 font-mono text-xs text-crypto-muted hover:bg-crypto-border/30 hover:text-crypto-text transition-colors"
      >
        <span aria-hidden="true">⬢</span>
        <span data-testid="version-chip-label">
          v{frontend.version}
          <span className="mx-1 text-crypto-border" aria-hidden="true">
            ·
          </span>
          {frontend.gitShaShort}
        </span>
        <span
          data-testid="version-health-dot"
          data-status={driftStatus}
          className={`inline-block h-2 w-2 rounded-full ${HEALTH_DOT_CLASS[driftStatus]}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <VersionDetailPopover
          frontend={frontend}
          backend={backend}
          driftStatus={driftStatus}
          placement={placement}
        />
      )}
    </div>
  )
}

VersionChipView.displayName = 'VersionChipView'
